import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { SendPaymentInstructionService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { SendPaymentInstructionModelService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.model.service';
import { format } from 'date-fns';
import { RegistrationStep2Service } from '@pg-app/client/services/registrationStep2.service';
import { Observable, forkJoin } from 'rxjs';
import { ShareService } from '@nttp/common-service/share-service';
import { UserRoleEnum } from '@nttp/enum/user-role.enum';
import { SendPaymentTypeEnum } from '@nttp/enum/send-payment-type.enum';
import { SendPaymentModalComponent } from '@pg-app/client/client-dashboard/components/send-payment-modal/send-payment-modal.component';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { UtilityService } from '@nttp/common-service/utility-service';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { DialogTypeEnum } from '@nttp/enum/common-dialog-type.enum';
import { Regex } from '@pg-app/shared/constants/regex';
import { UltimateRemitterComponent } from '@pg-app/client/client-dashboard/dialogs/ultimate-remitter/ultimate-remitter.component';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'np-payment-infomation',
  templateUrl: './payment-infomation.component.html',
  styleUrls: ['./payment-infomation.component.scss']
})
export class PaymentInfomationComponent implements OnInit, OnChanges {
  @Input()
  public availableCurrencies: any;

  public sendPaymentForm: FormGroup;
  public filteredRecipientObservable: Observable<Array<any>>;;
  public recipientList = [];
  public currencies: Array<any> = [];
  public rfpList = [];
  minDate: Date;
  notEnoughAmount = false;
  ADD_RECIPIENT = 'Add new beneficiary';
  userRoleEnum = UserRoleEnum;
  @Input() sendPaymentType = SendPaymentTypeEnum.SendPaymentDashboard;
  sendPaymentTypeEnum = SendPaymentTypeEnum;

  remitterDetailsRequired: boolean;
  countries: Array<any> = [];
  remitterDetails: FormGroup;
  recipientSelected: any = null;
  get getRecipient(): FormControl {
    return this.sendPaymentForm.get('recipient') as FormControl;
  }
  get getAmount(): FormControl {
    return this.sendPaymentForm.get('amount') as FormControl;
  }
  get getReasonPayment(): FormControl {
    return this.sendPaymentForm.get('reasonPayment') as FormControl;
  }
  get getCurrency(): FormControl {
    return this.sendPaymentForm.get('currency') as FormControl;
  }

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private sendPaymentInstructionService: SendPaymentInstructionService,
    private toasterService: ToastrService,
    private dialog: MatDialog,
    private paymentModel: SendPaymentInstructionModelService,
    public registrationService: RegistrationStep2Service,
    private shareService: ShareService,
    private utilityService: UtilityService,
    private fbUltimateRemitter: FormBuilder,
  ) {}

  ngOnInit() {
    this.sendPaymentForm = this.formBuilder.group({
      recipient: ['', Validators.required],
      amount: ['', Validators.required],
      reasonPayment: ['', Validators.required],
      currency: ['', Validators.required],
      paymentDate: ['', Validators.required]
    });

    this.minDate = this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade ? this.paymentModel.tradeValueDate : new Date();
    const defaultPaymentDate = (this.paymentModel.manageRecipientFromPayment && this.sendPaymentType === SendPaymentTypeEnum.SendPaymentDashboard) ||
      this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade
      ? this.paymentModel.paymentDate : this.minDate;
    this.sendPaymentForm.get('paymentDate').setValue(defaultPaymentDate);
    if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade) {
      this.getSendPaymentInfo();
    }

    this.shareService.getAccountType(true).subscribe(res => {
      this.remitterDetailsRequired = res.fullRemitterDetailsRequired;
    });

    this.getCountries();

    this.remitterDetails = this.fbUltimateRemitter.group({
      ultimateRemitterSelected: [null],

      remitterType: [null],
      remitterCompanyName: [null],
      remitterFirstName: [null],
      remitterLastName: [null],
      remitterAddress: [null],
      remitterCity: [null],
      remitterPostCode: [null],
      remitterState: [null],
      remitterCountry: [null],
      remitterCompanyNumber: [null],
      remitterDOB: [null]
    });

    this.filteredRecipientObservable = this.getRecipient.valueChanges.pipe(
      startWith(''),
      debounceTime(450),
      map(value => this.filterSearch(value))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('availableCurrencies' in changes) {
      this.availableCurrencies = changes.availableCurrencies.currentValue;
      if (!changes.availableCurrencies.firstChange) {
        this.getSendPaymentInfo();
      }
    }
  }

  public nextTab() {
    this.sendPaymentForm.markAllAsTouched();

    if (this.sendPaymentForm.valid) {
    // Check if payment currency is in trading day
    const paramCheckCurrencyIsInTradingDay = {
      currencyId: this.sendPaymentForm.get('currency').value,
      paymentDateString: format(this.sendPaymentForm.get('paymentDate').value, 'YYYYMMDD')
    };

    this.sendPaymentInstructionService.checkCurrencyIsInTradingDay(paramCheckCurrencyIsInTradingDay).subscribe(
      res => {
        if (res.error) {
          this.toasterService.error(res.error);
        } else {
          // Validate client, beneficiary and client balance
          this.validateData();
        }
      },
      () => {
        this.toasterService.error(CommonMessages.errorMessageAPI);
      }
    );
    }
  }

  validateData() {
    // Get all trades can link to this payment
    const params = {
      userId: null,
      currencyId: this.sendPaymentForm.get('currency').value,
      paymentDate: format(this.sendPaymentForm.get('paymentDate').value, 'YYYYMMDD')
    };
    const getTradeList = this.sendPaymentInstructionService.getPaymentInstructionData(params);

    // Get client balance for this payment currency
    const param = {
      userId: null,
      currencyId: this.sendPaymentForm.get('currency').value,
      totalsOnly: true
    };
    const getBalanceAmount = this.sendPaymentInstructionService.getBalanceAmount(param);
    const recipientId = this.recipientSelected.id;

    // check beneficiary valid or not base on ticket 3119
    // in case select a beneficiary
    const validateBeneficiary = this.recipientSelected.name === this.ADD_RECIPIENT ? null
      : this.sendPaymentInstructionService.validateBeneficiary(recipientId);

    const apiUpdateBankSEPA = this.sendPaymentInstructionService.updateBankSEPA({id: recipientId});

    // check account has default account group or not
    const isAccountOperational = this.utilityService.isAccountOperational();

    const selectedCurrency = this.currencies.find(item => item.id === this.getCurrency.value);
    const sepaCheckNeeded = selectedCurrency.currencyCode === 'EUR';

    const apis = sepaCheckNeeded ?
      validateBeneficiary ? [getTradeList, getBalanceAmount, isAccountOperational, validateBeneficiary, apiUpdateBankSEPA] :
        [getTradeList, getBalanceAmount, isAccountOperational]
    :
      validateBeneficiary ? [getTradeList, getBalanceAmount, isAccountOperational, validateBeneficiary] :
      [getTradeList, getBalanceAmount, isAccountOperational];

    forkJoin(apis).subscribe(results => {
      // If has no default  account group => return
      if (!results[0]) {
        this.toasterService.error(CommonMessages.accountNotOperational);
        return;
      }

      // If client balance + trade buy amount not enought => return
      const tradeList = results[0];
      let availableBalance = results[1];

      if (tradeList.length > 0) {
        availableBalance = availableBalance + tradeList.map(item => item.boughtOfferInfo.quantity).reduce((a, b) => a + b, 0);
      }

      if (parseFloat(this.sendPaymentForm.get('amount').value) > availableBalance) {
        if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentDashboard) {
          this.notEnoughAmount = true;
        } else {
          this.toasterService.error('Insufficient funds for payment. Please update payment amount.');
          this.getAmount.setValue(null);
        }
        return;
      }

      // Check if bank's SEPA status is updated successfully
      if (sepaCheckNeeded && apis.length >= 4 && !results[4].isSucceeded) {
        this.toasterService.error(results[4].error);
        return;
      }

      // Save information for next step
      this.paymentModel.reasonPayment = this.getReasonPayment.value;
      this.paymentModel.reasonPaymentOther = this.getReasonPayment.value.name === 'Others' ?
      this.sendPaymentForm.get('reasonPaymentOther').value : null;
      this.paymentModel.amount = parseFloat(this.getAmount.value);
      this.paymentModel.paymentDate = this.sendPaymentForm.get('paymentDate').value;

      const selectedCurrency = this.currencies.find(item => item.id === this.getCurrency.value);
      this.paymentModel.currency.code = selectedCurrency.currencyCode;
      this.paymentModel.currency.id = selectedCurrency.id;

      this.paymentModel.availableBalance = results[1];
      tradeList.forEach(item => {
        item.checked = false;
      });
      this.paymentModel.tradeList = tradeList;

      // Add new recipient from Send payment
      if (this.recipientSelected.name === this.ADD_RECIPIENT) {
        this.paymentModel.manageRecipientFromPayment = true;
        this.paymentModel.sendPaymentType = this.sendPaymentType;
        this.router.navigate(['client/send-payment/add-new-recipient']);
        if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade) {
          this.dialog.closeAll();
        }
      // beneficiary is invalid => force client goes to manage beneficiary to update
      } else if (results[3] && results[3].length > 0) {
        this.dialog.open(CommonDialogComponent, {
          data: {
            title: CommonMessages.invalidBeneficiary,
            dialogType: DialogTypeEnum.Success,
          },
          disableClose: true
        }).afterClosed().subscribe(() => {
          this.paymentModel.manageRecipientFromPayment = false;
          this.paymentModel.selectedRecipient = this.recipientSelected;
          this.paymentModel.sendPaymentType = this.sendPaymentType;
          this.router.navigate(['/client/manage-recipient'], { queryParams: { id: this.recipientSelected.id } });
          if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade) {
            this.dialog.closeAll();
          }
        });
      } else {
        // All good => next to step 2
        this.existingRecipientSelectedThenNext(tradeList);
      }
    });
  }

  private existingRecipientSelectedThenNext(tradeList) {
    this.paymentModel.selectedRecipient = this.recipientSelected;

    if (this.remitterDetailsRequired) {
      const dlgRef = this.dialog.open(UltimateRemitterComponent, {
        disableClose: true,
        panelClass: 'custom-dialog',
        data: {
          remitterDetails: this.remitterDetails,
          countries: this.countries
        }
      });

      dlgRef.afterClosed().subscribe(
        result => {
          if (result) {
            if (result.ultimateRemitterIdSelected) {
              this.paymentModel.ultimateRemitterId = result.ultimateRemitterIdSelected;
              this.showTradeListOrConfirmation(tradeList);
            } else {
              const param = {
                  type: result.ultimateRemitterType,
                  firstName: result.ultimateRemitterFirstName,
                  lastName: result.ultimateRemitterLastName,
                  dateOfBirthString: format(result.ultimateRemitterDOB, 'YYYYMMDD'),
                  companyName: result.ultimateRemitterCompanyName,
                  companyNumber: result.ultimateRemitterCompanyNumber,
                  address: result.ultimateRemitterAddress,
                  city: result.ultimateRemitterCity,
                  postCode: result.ultimateRemitterPostCode,
                  stateProvince: result.ultimateRemitterState,
                  countryId: result.ultimateRemitterCountryId
                }

                this.sendPaymentInstructionService.insertUltimateRemitter(param).subscribe(
                  response => {
                    if (response.isSucceeded && response.id) {
                      this.paymentModel.ultimateRemitterId = response.id;
                      this.showTradeListOrConfirmation(tradeList);
                    } else if (response.error) {
                      this.toasterService.error(response.error);
                    } else {
                      this.toasterService.error(CommonMessages.errorMessageAPI);
                    }
                })
            }
          }
        });
    } else {
      this.showTradeListOrConfirmation(tradeList);
    }
  }

  private showTradeListOrConfirmation(tradeList) {
    if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentDashboard) {
      this.showDialog();
    }
    this.paymentModel.step =
      tradeList.length > 0
        ? this.paymentModel.sendPaymentStep.balanceTradeAvailable
        : this.paymentModel.sendPaymentStep.paymentConfirmation;
  }

  private getSendPaymentInfo() {
    const getReasonPaymentList = this.shareService.reasonForPaymentList$;
    const getCurrencyList = this.sendPaymentInstructionService.getAllBalancesAndAvailableCurrencies({ userId: null });
    const getAllData = [getReasonPaymentList];
    let currencyResponseOrder = 1;
    let recipientResponseOrder = 2;

    // send payment from Dashboard will get data from Input variable with data from dashboard components
    // Send payment after create a trade need to call data from this API again
    if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade) {
      currencyResponseOrder = getAllData.length;
      getAllData.push(getCurrencyList);
    } else {
      this.currencies = this.availableCurrencies.filter(item => item.amount > 0) || [];
    }

    if (this.paymentModel?.currency?.id) {
      const param = {
        clientId: null,
        currencyId: this.paymentModel.currency.id
      };

      const getRecipientList = this.sendPaymentInstructionService.getSearchData(param);
      recipientResponseOrder = getAllData.length;
      getAllData.push(getRecipientList);
    }

    forkJoin(getAllData).subscribe(res => {
      this.rfpList = res[0] || [];
      if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade && res[1]) {
        this.currencies = res[currencyResponseOrder].availableCurrencies.filter(item => item.amount > 0) || [];
      }

      // Prepare data if fund account => send payment or send payment => add recipient => send payment
      if (this.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade || (this.paymentModel.manageRecipientFromPayment && this.sendPaymentType === SendPaymentTypeEnum.SendPaymentDashboard)) {
        // add new currency if fund account => send payment and currency not included in available currency
        const paymentCurrency = this.currencies.find(item => item.id === this.paymentModel.currency.id);
        if (!paymentCurrency) {
          this.currencies = this.currencies.concat({
            id: this.paymentModel.currency.id,
            currencyCode: this.paymentModel.currency.code
          });
        }
        // add new recipient if send payment => add recipient => send payment and is send one off recipient
        if (this.paymentModel?.currency?.id) {
          this.recipientList = res[recipientResponseOrder] || [];
        }
        if (this.paymentModel.newRecipient.id && !this.paymentModel.newRecipient.saveForFuture) {
          const checkExistRecipient = this.recipientList.find(item => item.id === this.paymentModel.newRecipient.id);
          if (!checkExistRecipient) {
            this.recipientList = this.recipientList.concat(this.paymentModel.newRecipient);
          }
        }

        // binding reason for payment if has value
        if (this.paymentModel.reasonPayment.id) {
          const reasonPayment = this.rfpList.find(
            item => item.id === this.paymentModel.reasonPayment.id
          );
          this.getReasonPayment.setValue(reasonPayment);
          if (reasonPayment.name === 'Others') {
            this.sendPaymentForm.addControl('reasonPaymentOther', new FormControl(this.paymentModel.reasonPaymentOther, [Validators.required, Validators.pattern(Regex.nonSwiftCharacters)]));
          }
        }

        // binding selected recipient if has value
        if (this.paymentModel.selectedRecipient.id) {
          const recipientDefault = this.recipientList.find(item => item.id === this.paymentModel.selectedRecipient.id);
          this.paymentModel.selectedRecipient = recipientDefault;
          this.recipientSelected = recipientDefault;
          this.getRecipient.setValue(recipientDefault.name);
        }

        this.getAmount.setValue(this.paymentModel.amount);
        this.getCurrency.setValue(this.paymentModel.currency.id);
        this.changeCurrency({value: this.paymentModel.currency.id});
        this.paymentModel.manageRecipientFromPayment = false;
      }
    });
  }

  private filterSearch(value: any): any[] {
    const filterValue = !value ? '' : value.toLowerCase();
    const result = this.recipientList.filter(item => item.currencyId === this.paymentModel.currency.id).filter(option => {
      return option.name.toLowerCase().indexOf(filterValue) !== -1;
    });
    return result;
  }

  selectRecipient(event) {
    this.getRecipient.setValue(event.option.value.name);
    this.recipientSelected = event.option.value;
  }

  public showDialog() {
    this.dialog.open(SendPaymentModalComponent, {
      disableClose: true,
      panelClass: 'custom-dialog'
    });
  }

  selectReasonChange(event) {
    if (event.value.name === 'Others') {
      this.sendPaymentForm.addControl('reasonPaymentOther', new FormControl(null, [Validators.required, Validators.pattern(Regex.nonSwiftCharacters)]));
    } else {
      this.sendPaymentForm.removeControl('reasonPaymentOther');
    }
  }

  changeCurrency(event) {
    this.paymentModel.currency.id = event.value;
    const param = {
      clientId: null,
      currencyId: this.paymentModel.currency.id
    };

    this.sendPaymentInstructionService.getSearchData(param).subscribe(res => {
      this.recipientList = res || [];
      this.getRecipient.setValue(null);
    });
  }

  fundAccount() {
    this.notEnoughAmount = false;
    this.getAmount.setValue('');
    const buyAmount = document.getElementById('buyAmountFocus');
    buyAmount.focus();
  }

  changeAmount() {
    const amount = this.getAmount.value;

    if (amount.trim() === '' || amount === null) {
      return;
    }

    if (parseFloat(amount) <= 0) {
      this.getAmount.setValue(parseFloat(amount).toFixed(2));
      this.getAmount.setErrors({ invalidAmount: true });
      return;
    }

    this.getAmount.setValue(parseFloat(amount).toFixed(2));
  }

  goBack() {
    if (this.paymentModel.recipientMandate && this.paymentModel.recipientMandate.id) {
      this.paymentModel.step = this.paymentModel.sendPaymentStep.paymentMandate;
    } else {
      this.dialog.closeAll();
      window.location.reload();
    }
  }

  private getCountries() {
    this.shareService.countryList$.subscribe(
      data => {
        data.forEach(item => {
          const flagIcon = (item.code.length <= 2 ? item.code : item.code.substring(0, 2)).toLowerCase();
          item.flag = `flag-icon flag-icon-${flagIcon}`;
        });
        this.countries = data || [];
      },
    );
  }
}
